/**
 * Sprint V103.ch · Block A — Challenge-Tag-Seite.
 *
 * Route: /challenge/tag/:day
 *
 * Zeigt den Impuls, das Freitext-Feld, "frueher gebaute" Entries darueber.
 *
 * Gates (via /api/challenge/day/:day):
 *  - 404 wenn kein Challenge-Member -> Redirect Dashboard
 *  - 423 wenn Tag noch gesperrt -> Teaser-Variante
 *  - 403 wenn write_locked (Trial abgebrochen oder abgelaufen) -> Lesemodus
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Lock, Loader2, Save, ArrowLeft, Clock, AlertTriangle } from "lucide-react";
import { trackChallenge } from "@/lib/ga4";
import { VimeoEmbed } from "@/components/challenge/VimeoEmbed";
import { ChallengeDayNav } from "@/components/challenge/ChallengeDayNav";

export default function ChallengeTag() {
  const { day: dayParam } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const authLoading = user === null;
  const day = parseInt(dayParam, 10);

  const [state, setState] = useState({ loading: true });
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saveError, setSaveError] = useState(null);

  // Lade Tag-Daten (nach Auth)
  const loadDay = useCallback(async () => {
    try {
      const { data } = await api.get(`/challenge2/day/${day}`);
      setState({ loading: false, data });
      trackChallenge("day_started", { day });
      setDraft(data?.entry?.text || "");
      setSavedAt(data?.entry?.updated_at || null);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 404) {
        setState({ loading: false, error: "not_member", detail });
      } else if (status === 423) {
        setState({ loading: false, error: "locked", detail });
      } else if (status === 401) {
        navigate(`/login?next=/challenge/tag/${day}`);
      } else {
        setState({ loading: false, error: "unknown", detail });
      }
    }
  }, [day, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/login?next=/challenge/tag/${day}`);
      return;
    }
    if (isNaN(day) || day < 1 || day > 5) {
      navigate("/dashboard");
      return;
    }
    loadDay();
  }, [authLoading, user, day, loadDay, navigate]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.post(`/challenge2/day/${day}/entry`, { text: draft });
      if (!savedAt && draft.trim()) trackChallenge("day_completed", { day });
      setSavedAt(new Date().toISOString());
      setSaving(false);
      // Timeline reload (fuer Live-Update der "past_entries"-Anzeige, falls Nutzer vorblaettert)
      // Nicht reload: nur lokalen Zustand aktualisieren
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setSaveError(
        detail === "write_locked_after_trial_end"
          ? "Dein Testzeitraum ist beendet &mdash; das Feld ist nur noch Lesemodus."
          : "Speichern fehlgeschlagen. Bitte erneut versuchen.",
      );
      setSaving(false);
    }
  };

  // --- Loading State ---
  if (authLoading || state.loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950"
        data-testid="challenge-tag-page"
        data-view-state="loading"
      >
        <Loader2 className="w-10 h-10 animate-spin text-[#F3651D]" />
      </div>
    );
  }

  // --- Error: kein Member (NEGATIVTEST) ---
  // Hier landet jemand ohne challenge_joined_at.
  // Wir rendern NICHT die Challenge-UI, sondern leiten freundlich zum Dashboard.
  if (state.error === "not_member") {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4"
        data-testid="challenge-tag-page"
        data-view-state="not-member"
      >
        <div className="max-w-md text-center bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-lg border border-slate-200 dark:border-slate-800">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
            Dieser Bereich gehört zur 5-Tage-Challenge.
          </h1>
          <p className="text-sm text-slate-700 dark:text-slate-300 mb-4">
            Du bist aktuell nicht angemeldet. Zurück in den Mitgliederbereich?
          </p>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-2 rounded-xl bg-[#F3651D] hover:bg-[#d95415] text-white font-bold px-5 py-3"
            data-testid="challenge-tag-not-member-cta"
          >
            <ArrowLeft className="w-4 h-4" /> Zum Mitgliederbereich
          </button>
        </div>
      </div>
    );
  }

  // --- Error: Tag noch gesperrt ---
  if (state.error === "locked") {
    const unlockAt = state.detail?.unlock_at;
    const unlockLabel = unlockAt
      ? new Date(unlockAt).toLocaleString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
      : null;
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-amber-50 dark:from-slate-950 dark:to-slate-900 px-4"
        data-testid="challenge-tag-page"
        data-view-state="locked"
      >
        <div className="max-w-lg text-center bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-lg border border-slate-200 dark:border-slate-800">
          <Lock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            Tag {day} öffnet in Kürze.
          </h1>
          <p className="text-sm text-slate-700 dark:text-slate-300 mb-6">
            {unlockLabel ? <>Freischaltung: <b className="text-slate-900 dark:text-white" data-testid="challenge-tag-unlock-at">{unlockLabel}</b>.</> : "Jeden Tag öffnet sich ein neuer Tag."}
          </p>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-200 dark:bg-slate-800 text-slate-900 dark:text-white font-bold px-5 py-3"
            data-testid="challenge-tag-locked-cta"
          >
            <ArrowLeft className="w-4 h-4" /> Zurück zum Mitgliederbereich
          </button>
        </div>
      </div>
    );
  }

  const data = state.data;
  const pastEntries = data?.past_entries || [];
  const writeLocked = Boolean(data?.write_locked);
  const savedLabel = savedAt
    ? new Date(savedAt).toLocaleString("de-DE", { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50/40 to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950"
      data-testid="challenge-tag-page"
      data-view-state="active"
      data-day={day}
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">

        {/* Breadcrumb */}
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 inline-flex items-center gap-1 mb-6"
          data-testid="challenge-tag-breadcrumb"
        >
          <ArrowLeft className="w-3 h-3" /> Mitgliederbereich
        </button>

        {/* Timeline: "Was du bisher gebaut hast" (Punkt 3 — sichtbares Wachsen) */}
        {pastEntries.length > 0 && (
          <div
            className="mb-8 rounded-2xl bg-white/60 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 p-5 sm:p-6"
            data-testid="challenge-tag-timeline"
          >
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">
              Was du bisher gebaut hast
            </p>
            <ul className="space-y-4">
              {pastEntries.map((e) => (
                <li key={e.day} className="flex gap-3" data-testid={`challenge-tag-past-entry-${e.day}`}>
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#F3651D]/10 text-[#F3651D] text-xs font-bold flex items-center justify-center">
                    {e.day}
                  </div>
                  <div className="flex-1 text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
                    {e.text}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Haupt-Karte: Impuls + Freitext */}
        <div
          className="rounded-2xl bg-white dark:bg-slate-900 border-2 border-[#F3651D]/30 shadow-xl overflow-hidden"
          data-testid="challenge-tag-card"
        >
          <div className="p-6 sm:p-8">
            <p className="text-xs font-bold uppercase tracking-widest text-[#F3651D] mb-2">
              {data?.impuls || `TAG ${day}`}
            </p>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mb-4" data-testid="challenge-tag-title">
              {data?.title || `Tag ${day}`}
            </h1>
            {data?.video_url ? <div className="mb-5"><VimeoEmbed url={data.video_url} title={data?.title} testId="challenge-tag-video" /></div> : null}
            <p className="text-base text-slate-700 dark:text-slate-200 leading-relaxed mb-5 whitespace-pre-line" data-testid="challenge-tag-frage">
              {data?.frage}
            </p>

            <textarea
              value={draft}
              onChange={(e) => !writeLocked && setDraft(e.target.value)}
              placeholder={data?.placeholder || "Schreib hier dein Material …"}
              disabled={writeLocked}
              data-testid="challenge-tag-textarea"
              rows={12}
              className="w-full rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-4 py-3 text-base leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#F3651D] focus:border-[#F3651D] disabled:opacity-70 disabled:cursor-not-allowed"
            />

            <div className="mt-3 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
              <span>{draft.length} Zeichen</span>
              {savedLabel && !saving && !saveError && (
                <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="w-3 h-3" /> Gespeichert {savedLabel}
                </span>
              )}
            </div>

            {writeLocked && (
              <div
                className="mt-4 rounded-xl bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-4 py-3 text-sm text-amber-900 dark:text-amber-200 flex items-start gap-2"
                data-testid="challenge-tag-write-locked"
              >
                <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Dein Testzeitraum ist beendet &mdash; die Challenge-Felder sind nur noch im Lesemodus.
                  Dein Material bleibt dir, du kannst es weiter sehen.
                </span>
              </div>
            )}

            {saveError && (
              <div
                className="mt-4 rounded-xl bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200"
                data-testid="challenge-tag-save-error"
              >
                {saveError}
              </div>
            )}

            <button
              type="button"
              onClick={handleSave}
              disabled={saving || writeLocked || draft.trim().length === 0}
              data-testid="challenge-tag-save-btn"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#F3651D] hover:bg-[#d95415] disabled:opacity-40 disabled:cursor-not-allowed text-white font-extrabold text-base px-6 py-4 shadow-md transition-all"
            >
              {saving ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Speichere …</>
              ) : (
                <><Save className="w-5 h-5" /> Mein Material speichern</>
              )}
            </button>
          </div>
        </div>
        <ChallengeDayNav day={day} state={data?.state} />
      </div>
    </div>
  );
}
